import { ChangeEvent, ComponentProps, useEffect, useState } from "react";

import classNames from "classnames/bind";
import { PaginationHandler } from "utils/paginationHandler";

import Dropdown, { Option } from "../Dropdown";
import UIIcon from "../UIIcon";
import style from "./GenericPagination.module.scss";

const cx = classNames.bind(style);

type GenericPaginationType<T> = {
    items: T[];
    itemsPerPage: number;
    CurrentPageItemsEvent: (e: T[]) => void;
} & ComponentProps<"div">;

export function GenericPagination<T>({
    items,
    itemsPerPage,
    CurrentPageItemsEvent,
    className,
    ...props
}: GenericPaginationType<T>) {
    const [paginator] = useState(new PaginationHandler<T>(items, itemsPerPage));
    const [selectedPage, setSelectedPage] = useState<number>(1);

    const handleNextPage = () => {
        paginator.nextPage();
        setSelectedPage(paginator.currentPage);
        CurrentPageItemsEvent(paginator.getCurrentPageItems());
        window.scrollTo(0, 0);
    };

    const handlePrevPage = () => {
        paginator.prevPage();
        setSelectedPage(paginator.currentPage);
        CurrentPageItemsEvent(paginator.getCurrentPageItems());
        window.scrollTo(0, 0);
    };

    const dropdownOptions = (): Option[] => {
        return paginator.paginatedItems.map((_item, i) => ({
            label: `${i + 1} of ${paginator.totalPages}`,
            value: i + 1,
        }));
    };

    const handleSelectPageChange = (event: ChangeEvent<HTMLSelectElement>) => {
        paginator.goToPage(+event.target.value);
        setSelectedPage(+event.target.value);
        CurrentPageItemsEvent(paginator.getCurrentPageItems());
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        CurrentPageItemsEvent(paginator.getCurrentPageItems());
    }, []);

    return (
        <div className={cx("genericPagination", className)} {...props}>
            <div className={cx("genericPagination-action")}>
                <button
                    className={cx(
                        "genericPagination-action--previous",
                        !paginator.hasPrevPage() && "genericPagination-action--previous-hide"
                    )}
                    onClick={handlePrevPage}
                >
                    <UIIcon icon="ChevronLeft" bgColor="#E3E9EC" /> Previous
                </button>
                <div className={cx("genericPagination-action--dropdown")}>
                    <Dropdown
                        options={dropdownOptions()}
                        defaultValue={1}
                        value={selectedPage}
                        onChange={handleSelectPageChange}
                    />
                </div>
                <button
                    className={cx(
                        "genericPagination-action--next",
                        !paginator.hasNextPage() && "genericPagination-action--previous-hide"
                    )}
                    onClick={handleNextPage}
                >
                    Next <UIIcon icon="ChevronRight" bgColor="#E3E9EC" />
                </button>
            </div>
            <div className={cx("genericPagination-counter")}>
                {`Showing ${paginator.getPaginationInfo().start} - ${
                    paginator.getPaginationInfo().end
                } of ${paginator.totalItems} results`}
            </div>
        </div>
    );
}
